import { useState, useEffect, forwardRef } from 'react'
import { Helmet } from 'react-helmet-async'
import TerminalDialog from '../terminal/terminalDialog'
import MainCard, {
  SnackBarHideDuration,
  SnackBarTransition,
  ShowNoRecordsFound,
  ErrorScreen
} from '../../../utils/ui_utils'
import { businessCheck } from '../../../utils/utils'

import {
  Grid,
  Snackbar,
  Card,
  Divider,
  Typography,
  Alert as MuiAlert,
  Pagination,
  CardContent,
  Box,
  Chip,
  IconButton,
  TextField,
  Button
} from '@mui/material'
import moment from 'moment'

import { useNavigate } from 'react-router-dom'
import UserTypeShimmerWithoutAvatar from '../../../utils/ui_components/shimmers/userTypeShimmerWithoutAvatar'
import { FileCopyOutlined } from '@mui/icons-material'
import {
  useCreateTerminalOffline,
  useFetchTerminalOffline,
  useUpdateTerminalOffline
} from '../../../modules/terminals/terminalOfflineHooks'

function TerminalOffline() {
  const [terminalData, setTerminalData] = useState(null)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(15)
  const [pagecount, setPageCount] = useState(1)
  const [goToPageInput, setGoToPageInput] = useState('')
  const [terminalDrawerMode, setterminalDrawerMode] = useState('edit')
  const [terminalDrawerVisibility, setTerminalDrawerVisibility] =
    useState(false)
  const [snackbar, setSnackBar] = useState(false)
  const [PopUpMessage, setPopUpMessage] = useState(null)
  const [severity, setSeverity] = useState(null)

  const {
    fetchTerminalOffline,
    fetchTerminalOfflineData,
    fetchTerminalOfflineError,
    fetchTerminalOfflineLoading
  } = useFetchTerminalOffline()
  const { createTerminalOffline } = useCreateTerminalOffline()
  const { updateTerminalOffline } = useUpdateTerminalOffline()

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
  })

  const handlePageChange = async (event, value) => {
    setPage(value)
    let payLoad = {
      pageNumber: value,
      limit: limit
    }
    fetchTerminalOffline(payLoad)
  }

  const handleGoToPageChange = (event) => {
    const value = event.target.value
    if (/^\d+$/.test(value) || value === '') {
      setGoToPageInput(value)
    }
  }

  const handleGoToPageSubmit = () => {
    const pageNum = parseInt(goToPageInput, 10)
    if (pageNum >= 1 && pageNum <= pagecount) {
      handlePageChange(null, pageNum)
      setGoToPageInput('')
    }
  }

  const handleGoToPageKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleGoToPageSubmit()
    }
  }

  const history = useNavigate()
  const handleSnackbarClose = () => {
    setSnackBar(false)
  }
  const toastMessage = (message, severity) => {
    setPopUpMessage(message)
    setSeverity(severity)
    setSnackBar(true)
  }

  const pageCount = (data1) => {
    let count = Math.ceil(data1 / limit)
    setPageCount(count)
  }

  useEffect(() => {
    businessCheck(history)
    let payLoad = {
      pageNumber: page,
      limit: limit
    }
    fetchTerminalOffline(payLoad)
  }, [])

  useEffect(() => {
    if (fetchTerminalOfflineData.data?.length > 0) {
      pageCount(fetchTerminalOfflineData.total)
    }
  }, [fetchTerminalOfflineData])

  const fetchTerminalOnError = () => {
    let payLoad = {
      pageNumber: page,
      limit: limit
    }
    fetchTerminalOffline(payLoad)
  }

  const TerminalListUI = ({ terminal, index }) => (
    <MainCard
      onClick={() => {
        setterminalDrawerMode('edit')
        setTerminalDrawerVisibility(true)
        setTerminalData(terminal)
      }}
      sx={{
        mt: 1,
        p: 2,
        alignItems: 'center',
        transition: 'box-shadow 0.3s ease',
        borderRadius: 1,
        '&:hover': {
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
          backgroundColor: 'rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      <Grid container column={6} spacing={1}>
        <Grid item xs md>
          <Grid
            container
            direction={'column'}
            sx={{ pl: 1, cursor: 'pointer' }}
          >
            <Grid item>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {terminal?.name}
              </Typography>
            </Grid>
            <Grid item>
              {terminal?.serial_number ? (
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  SL No : {terminal?.serial_number}
                </Typography>
              ) : (
                <Typography>&nbsp;</Typography>
              )}
            </Grid>
            <Grid item>
              {terminal?.app_flavor?.flavor_name ? (
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  For {terminal?.app_flavor?.flavor_name}
                </Typography>
              ) : (
                <Typography>&nbsp;</Typography>
              )}
            </Grid>
            {terminal?.active && (
              <>
                {terminal?.activated_on && (
                  <Grid item>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                      Activated on: {moment(terminal?.activated_on).format('DD-MM-YYYY')}
                    </Typography>
                  </Grid>
                )}
                {terminal?.expiry_date && (
                  <Grid item>
                    <Typography
                      variant="body1"
                      sx={{
                        color: moment(terminal?.expiry_date).isBefore(moment())
                          ? 'red'
                          : 'green'
                      }}
                    >
                      {moment(terminal?.expiry_date).isBefore(moment())
                        ? 'Expired: '
                        : 'Subscription ends on: '}
                      {moment(terminal?.expiry_date).format('DD-MM-YYYY')}
                    </Typography>
                  </Grid>
                )}
                {terminal?.warranty_expiry_date && (
                  <Grid item>
                    <Typography
                      variant="body1"
                      sx={{
                        color: moment(terminal?.warranty_expiry_date).isBefore(moment())
                          ? 'red'
                          : 'text.secondary'
                      }}
                    >
                      {moment(terminal?.warranty_expiry_date).isBefore(moment())
                        ? 'Warranty expired: '
                        : 'Warranty expires on: '}
                      {moment(terminal?.warranty_expiry_date).format('DD-MM-YYYY')}
                    </Typography>
                  </Grid>
                )}
              </>
            )}
            <Grid item>
              <Typography
                variant="subtitle1"
                sx={{ mt: 3, color: '#379237' }}
                gutterBottom
              >
                {terminal?.activation_key.toString()}
                {
                  <IconButton
                    size="small"
                    aria-label="Copy Activation Key"
                    onClick={(event) => {
                      event.stopPropagation()
                      navigator.clipboard.writeText(
                        terminal?.activation_key.toString()
                      )
                      toastMessage('Activation Key Copied', 'success')
                    }}
                    sx={{ ml: 1, color: '#757575' }}
                  >
                    <FileCopyOutlined />
                  </IconButton>
                }
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          sx={{ display: 'flex', alignItems: 'center' }}
          onClick={() => {
            setterminalDrawerMode('edit')
            setTerminalDrawerVisibility(true)
            setTerminalData(terminal)
          }}
        >
          <Chip
            size="small"
            label={terminal?.active ? 'Activated' : 'Not Activated'}
            color={terminal?.active ? 'success' : 'error'}
          />
        </Grid>
      </Grid>
    </MainCard>
  )

  return (
    <>
      <Helmet title="Offline Terminals" />
      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Grid item md={6}>
          <Typography variant="h4" gutterBottom display="inline">
            Offline Terminals
          </Typography>
        </Grid>
        <Grid item>
          <TerminalDialog
            isOnlineTerminal={false}
            visible={terminalDrawerVisibility}
            title={
              terminalDrawerMode == 'new'
                ? 'Create New Terminal'
                : 'Edit Terminal'
            }
            initialValues={terminalData}
            okText={terminalDrawerMode == 'new' ? 'Create' : 'Update'}
            action={terminalDrawerMode}
            onCancel={() => setTerminalDrawerVisibility(false)}
            onSubmit={(value) => {
              let values = _.cloneDeep(value)
              if (values.app_flavor) {
                delete values.app_flavor
              }
              if (terminalDrawerMode == 'new') {
                try {
                  createTerminalOffline(values, (success, error, message) => {
                    if (error) {
                      setTerminalDrawerVisibility(true)
                      toastMessage(message, 'error')
                    } else {
                      setTerminalDrawerVisibility(false)
                      toastMessage('Terminal created successfully', 'success')
                      let payLoad = {
                        pageNumber: page,
                        limit: limit
                      }
                      fetchTerminalOffline(payLoad)
                    }
                  })
                } catch (error) {
                  console.error(error)
                }
              } else {
                if (values.app_flavor) {
                  delete values.app_flavor
                }
                values.id = terminalData?._id
                delete values._id
                delete values.business_id
                delete values.outlet
                delete values.key
                try {
                  updateTerminalOffline(values, (success, error, message) => {
                    if (error) {
                      setTerminalDrawerVisibility(true)
                      toastMessage(message?.error.name, 'error')
                    } else {
                      setTerminalDrawerVisibility(false)
                      toastMessage('Terminal updated successfully', 'success')
                      let payLoad = {
                        pageNumber: page,
                        limit: limit
                      }
                      fetchTerminalOffline(payLoad)
                    }
                  })
                } catch (error) {
                  console.error(error)
                }
              }
            }}
          />
        </Grid>
      </Grid>
      <Divider sx={{ marginTop: 5 }} />

      {fetchTerminalOfflineError ? (
        <ErrorScreen
          onClick={() => {
            fetchTerminalOnError()
          }}
        />
      ) : (
        <div>
          {!fetchTerminalOfflineLoading &&
            fetchTerminalOfflineData.data &&
            fetchTerminalOfflineData.data?.length <= 0 && (
              <ShowNoRecordsFound />
            )}

          {!fetchTerminalOfflineError &&
            !fetchTerminalOfflineLoading &&
            fetchTerminalOfflineData.data &&
            fetchTerminalOfflineData.data?.length > 0 && (
              <Grid container spacing={10} sx={{ paddingTop: 5 }}>
                <Grid item xs sm md>
                  <Box sx={{ margin: 2 }}>
                    <Card raised sx={{ borderRadius: 3 }}>
                      <CardContent>
                        <Grid
                          container
                          columns={6}
                          rowSpacing={3}
                          columnSpacing={3}
                        >
                          {fetchTerminalOfflineData.data?.map((row, index) => (
                            <Grid item xs={6} sm={3} md={2} key={index}>
                              <TerminalListUI terminal={row} />
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                      {pagecount > 1 && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            p: 2,
                            backgroundColor: 'grey.50',
                            borderRadius: 1
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              width: '100%'
                            }}
                          >
                            <Pagination
                              count={pagecount}
                              page={page}
                              color="secondary"
                              size="small"
                              boundaryCount={2}
                              siblingCount={0}
                              showFirstButton
                              showLastButton
                              onChange={handlePageChange}
                              sx={{
                                '& .MuiPaginationItem-root': {
                                  transition: 'all 0.2s ease-in-out'
                                }
                              }}
                            />
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gap: 1.5,
                              backgroundColor: 'white',
                              p: 2,
                              borderRadius: 1,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                              transition: 'box-shadow 0.2s ease-in-out',
                              '&:hover': {
                                boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
                              }
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', fontWeight: 500 }}
                            >
                              Jump to
                            </Typography>
                            <TextField
                              size="small"
                              value={goToPageInput}
                              onChange={handleGoToPageChange}
                              onKeyPress={handleGoToPageKeyPress}
                              placeholder="Page"
                              sx={{
                                width: '80px',
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: 'white',
                                  transition: 'all 0.2s ease-in-out',
                                  '& fieldset': {
                                    borderColor: 'grey.300',
                                    borderWidth: '1px'
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'secondary.main',
                                    borderWidth: '1.5px'
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: 'secondary.main',
                                    borderWidth: '2px',
                                    boxShadow:
                                      '0 0 0 3px rgba(156, 39, 176, 0.1)'
                                  }
                                },
                                '& .MuiOutlinedInput-input': {
                                  textAlign: 'center',
                                  fontWeight: 500,
                                  padding: '7px 8px',
                                  height: '20px',
                                  lineHeight: '20px'
                                }
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', fontWeight: 400 }}
                            >
                              of {pagecount}
                            </Typography>
                            <Button
                              variant="contained"
                              color="secondary"
                              size="small"
                              onClick={handleGoToPageSubmit}
                              disabled={
                                !goToPageInput ||
                                parseInt(goToPageInput, 10) < 1 ||
                                parseInt(goToPageInput, 10) > pagecount
                              }
                              sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                px: 2,
                                py: 0.75,
                                height: '34px',
                                lineHeight: 1,
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 4px 8px rgba(156, 39, 176, 0.3)'
                                },
                                '&:active': {
                                  transform: 'translateY(0)'
                                }
                              }}
                            >
                              Go
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Card>
                  </Box>
                </Grid>
              </Grid>
            )}
          <div>
            <Snackbar
              open={snackbar}
              autoHideDuration={SnackBarHideDuration}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              onClose={handleSnackbarClose}
              TransitionComponent={SnackBarTransition}
            >
              <Alert onClose={handleSnackbarClose} severity={severity}>
                {PopUpMessage}
              </Alert>
            </Snackbar>
          </div>
        </div>
      )}

      {fetchTerminalOfflineLoading && <UserTypeShimmerWithoutAvatar />}
    </>
  )
}
export default TerminalOffline
